import { axiosInstance } from "./apiClient";

// Long-running processing calls need a much higher timeout than the default 10 s.
const PROCESSING_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

const TOKEN_KEY = "access_token";

export async function login(
  username: string,
  password: string
): Promise<string> {
  try {
    const response = await axiosInstance.post("/auth/login", {
      username,
      password,
    });
    const token = response.data.access_token;
    localStorage.setItem(TOKEN_KEY, token);
    return token;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.detail || error.message || "Login failed";
    throw new Error(errorMessage);
  }
}

export function logout(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return !!localStorage.getItem(TOKEN_KEY);
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export interface CurrentUser {
  id: string;
  email: string;
  firstname?: string | null;
  lastname?: string | null;
  role?: string;
  is_active: boolean;
  is_verified: boolean;
}

export async function updateCurrentUserProfile(payload: {
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
}): Promise<CurrentUser> {
  const response = await axiosInstance.patch<CurrentUser>("/auth/me", payload);
  return response.data;
}

export async function changeCurrentUserPassword(payload: {
  current_password: string;
  new_password: string;
  new_password_confirm: string;
}): Promise<{ message: string }> {
  const response = await axiosInstance.post<{ message: string }>(
    "/auth/change-password",
    payload
  );
  return response.data;
}

// ── Distance calculator service ────────────────────────────────────────

/** Upload the file and start background processing. Returns the job_id. */
export async function startDistancesJob(
  formData: FormData
): Promise<{ job_id: string }> {
  const response = await axiosInstance.post("/distances/start", formData, {
    timeout: 60_000,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data as { job_id: string };
}

/**
 * Ticket monouso (breve, legato al job) per aprire lo stream SSE.
 * EventSource non può impostare l'header Authorization: invece dell'access token vero,
 * in query string si passa questo ticket.
 */
export async function getDistancesStreamTicket(jobId: string): Promise<string> {
  const response = await axiosInstance.post(
    `/distances/stream-ticket/${encodeURIComponent(jobId)}`
  );
  return response.data.ticket as string;
}

/** Download the processed Excel file once the job is done. */
export async function downloadDistancesResult(
  jobId: string
): Promise<{ blob: Blob; filename: string }> {
  const response = await axiosInstance.get(`/distances/result/${jobId}`, {
    responseType: "blob",
    timeout: 30_000,
  });
  const disposition: string = response.headers["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  const filename = match ? match[1] : "risultato.xlsx";
  return { blob: response.data as Blob, filename };
}

// ── Database tratte (distances_cache): vista tabellare + modifica manuale ──

export interface DistanceEntry {
  cache_key_l1: string;
  a_azienda?: string | null;
  a_location?: string | null;
  a_location_lat?: number | null;
  a_location_lon?: number | null;
  a_porto?: string | null;
  a_porto_lat?: number | null;
  a_porto_lon?: number | null;
  b_azienda?: string | null;
  b_location?: string | null;
  b_location_lat?: number | null;
  b_location_lon?: number | null;
  b_porto?: string | null;
  b_porto_lat?: number | null;
  b_porto_lon?: number | null;
  vettore?: string | null;
  alert?: string | null;
  assunzioni?: string | null;
  km_gomma?: number | null;
  km_treno?: number | null;
  km_nave?: number | null;
  km_aereo?: number | null;
  first_mile?: number | null;
  last_mile?: number | null;
}

export interface DistanceEntryListResponse {
  items: DistanceEntry[];
  total: number;
  page: number;
  page_size: number;
}

export async function listDistanceEntries(params: {
  q?: string;
  vettore?: string;
  has_alert?: boolean;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_dir?: string;
}): Promise<DistanceEntryListResponse> {
  const response = await axiosInstance.get("/distances/entries", { params });
  return response.data;
}

export async function updateDistanceEntry(
  cacheKey: string,
  data: Record<string, unknown>
): Promise<DistanceEntry> {
  const response = await axiosInstance.put(
    `/distances/entries/${encodeURIComponent(cacheKey)}`,
    data
  );
  return response.data;
}

export async function deleteDistanceEntry(cacheKey: string): Promise<void> {
  await axiosInstance.delete(`/distances/entries/${encodeURIComponent(cacheKey)}`);
}

// ── Mappature porti/aeroporti (port_mappings) ────────────────────────────

export type PortMappingKind = "PORTO" | "AEREO";
export type PortMappingSource = "SEED" | "CUSTOM";

export interface PortMapping {
  id: string;
  code: string;
  name: string;
  kind: PortMappingKind;
  country_code?: string | null;
  source: PortMappingSource;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface PortMappingListResponse {
  items: PortMapping[];
  total: number;
  page: number;
  page_size: number;
}

export async function listPortMappings(params: {
  q?: string;
  kind?: string;
  country?: string;
  page?: number;
  page_size?: number;
}): Promise<PortMappingListResponse> {
  const response = await axiosInstance.get("/port-mappings", { params });
  return response.data;
}

export async function createPortMapping(data: {
  code: string;
  name: string;
  kind: PortMappingKind;
  country_code?: string | null;
}): Promise<PortMapping> {
  const response = await axiosInstance.post("/port-mappings", data);
  return response.data;
}

export async function updatePortMapping(
  id: string,
  data: Record<string, unknown>
): Promise<PortMapping> {
  const response = await axiosInstance.put(`/port-mappings/${id}`, data);
  return response.data;
}

export async function deletePortMapping(id: string): Promise<void> {
  await axiosInstance.delete(`/port-mappings/${id}`);
}

export interface PortMappingSearchResult {
  code: string;
  name: string;
  kind: PortMappingKind;
  country_code?: string | null;
  score: number;
  confidence: "high" | "low" | "none";
}

export async function searchPortMappings(params: {
  q: string;
  kind?: string;
  country?: string;
  limit?: number;
}): Promise<PortMappingSearchResult[]> {
  const response = await axiosInstance.get("/port-mappings/search", { params });
  return response.data;
}

// ── Document comparator service ────────────────────────────────────────

export type CorrectionBreakdown = Record<string, number>;

export async function processComparison(formData: FormData): Promise<{
  blob: Blob;
  filename: string;
  corrections?: number;
  breakdown?: CorrectionBreakdown;
  unanalysedPages: number[];
}> {
  const response = await axiosInstance.post("/comparator/process", formData, {
    responseType: "blob",
    timeout: PROCESSING_TIMEOUT_MS,
    headers: { "Content-Type": "multipart/form-data" },
  });

  const disposition: string =
    response.headers["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  const filename = match ? match[1] : "annotato.pdf";

  const correctionsHeader = response.headers["x-correction-count"];
  const corrections = correctionsHeader
    ? parseInt(correctionsHeader, 10)
    : undefined;

  const breakdownHeader = response.headers["x-correction-breakdown"];
  let breakdown: CorrectionBreakdown | undefined;
  if (breakdownHeader) {
    try {
      const decoded = decodeURIComponent(breakdownHeader);
      const parsed = JSON.parse(decoded);
      if (parsed && typeof parsed === "object") {
        breakdown = parsed as CorrectionBreakdown;
      }
    } catch {
      // ignore malformed header
    }
  }

  const unanalysedHeader: string = response.headers["x-unanalysed-pages"] || "";
  const unanalysedPages = unanalysedHeader
    .split(",")
    .map((p) => parseInt(p, 10))
    .filter((p) => !Number.isNaN(p));

  return { blob: response.data as Blob, filename, corrections, breakdown, unanalysedPages };
}

// ── Document comparator: background job + polling ──────────────────────

export interface ComparisonResult {
  blob: Blob;
  filename: string;
  corrections?: number;
  breakdown?: CorrectionBreakdown;
  unanalysedPages: number[];
}

interface ComparisonStatus {
  status: "queued" | "processing" | "done" | "error";
  progress: number;
  message: string;
  error?: string;
  corrections?: number;
  breakdown?: CorrectionBreakdown;
  unanalysed_pages?: number[];
}

const COMPARISON_POLL_INTERVAL_MS = 3000;
const COMPARISON_MAX_WAIT_MS = 60 * 60 * 1000; // the server fails jobs before this
const COMPARISON_MAX_POLL_ERRORS = 10; // consecutive network failures tolerated

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Start the comparison in the background and poll until it ends. Unlike a single long
 * request, a dropped connection or a proxy timeout cannot lose the job.
 */
export async function processComparisonJob(
  formData: FormData,
  onProgress?: (progress: number, message: string, status: ComparisonStatus["status"]) => void
): Promise<ComparisonResult> {
  const start = await axiosInstance.post("/comparator/start", formData, {
    timeout: 120_000,
    headers: { "Content-Type": "multipart/form-data" },
  });
  const jobId: string = start.data.job_id;

  const startedAt = Date.now();
  let consecutiveErrors = 0;
  for (;;) {
    if (Date.now() - startedAt > COMPARISON_MAX_WAIT_MS) {
      throw new Error("Tempo massimo di attesa superato. Riprova con un documento più piccolo.");
    }
    await sleep(COMPARISON_POLL_INTERVAL_MS);

    let status: ComparisonStatus;
    try {
      const response = await axiosInstance.get<ComparisonStatus>(`/comparator/status/${jobId}`, {
        timeout: 20_000,
      });
      status = response.data;
      consecutiveErrors = 0;
    } catch (err: any) {
      const code = err?.response?.status;
      if (code === 404) {
        throw new Error("Il confronto non è più disponibile (scaduto o riavviato il server). Rilancialo.");
      }
      consecutiveErrors += 1;
      if (consecutiveErrors >= COMPARISON_MAX_POLL_ERRORS) throw err;
      continue;
    }

    onProgress?.(status.progress, status.message, status.status);
    if (status.status === "error") {
      throw new Error(status.error || "Errore durante il confronto");
    }
    if (status.status === "done") {
      const result = await axiosInstance.get(`/comparator/result/${jobId}`, {
        responseType: "blob",
        timeout: 120_000,
      });
      const disposition: string = result.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      return {
        blob: result.data as Blob,
        filename: match ? match[1] : "annotato.pdf",
        corrections: status.corrections,
        breakdown: status.breakdown,
        unanalysedPages: status.unanalysed_pages ?? [],
      };
    }
  }
}
